import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { STORAGE_KEYS } from '../../../config/storageKeys';

export const STORAGE_KEY_TASKS = 'pathbuddy_appstore_tasks';
export const STORAGE_KEY_BUBBLES = 'pathbuddy_appstore_bubbles';

function userScopedKey(baseKey, userStorageId) {
  if (!userStorageId) return baseKey;
  return `${baseKey}:${userStorageId}`;
}

/** For addBubbleToStorage (outside React); matches provider keying. */
export function getAppStoreUserStorageId() {
  try {
    const raw =
      (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEYS.USER_DATA)) ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STORAGE_KEYS.USER_DATA));
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.id || u?.email || null;
  } catch (_) {
    return null;
  }
}
const MAX_TASKS = 3;

const BUBBLE_WIDTH = 200;
const BUBBLE_HEIGHT = 72;
const BUBBLE_GAP = 16;
const BUBBLE_COLS = 3;

function getDefaultPosition(index) {
  const col = index % BUBBLE_COLS;
  const row = Math.floor(index / BUBBLE_COLS);
  return {
    x: col * (BUBBLE_WIDTH + BUBBLE_GAP),
    y: row * (BUBBLE_HEIGHT + BUBBLE_GAP),
  };
}

function rectsOverlap(a, b) {
  return (
    a.x < b.x + BUBBLE_WIDTH + BUBBLE_GAP &&
    a.x + BUBBLE_WIDTH + BUBBLE_GAP > b.x &&
    a.y < b.y + BUBBLE_HEIGHT + BUBBLE_GAP &&
    a.y + BUBBLE_HEIGHT + BUBBLE_GAP > b.y
  );
}

function pushOut( moved, other ) {
  const a = { x: moved.x, y: moved.y };
  const b = { x: other.x, y: other.y };
  const overlapLeft = b.x + BUBBLE_WIDTH + BUBBLE_GAP - a.x;
  const overlapRight = a.x + BUBBLE_WIDTH + BUBBLE_GAP - b.x;
  const overlapTop = b.y + BUBBLE_HEIGHT + BUBBLE_GAP - a.y;
  const overlapBottom = a.y + BUBBLE_HEIGHT + BUBBLE_GAP - b.y;
  if (overlapLeft <= 0 || overlapRight <= 0 || overlapTop <= 0 || overlapBottom <= 0) return { x: other.x, y: other.y };
  const shiftX = overlapLeft < overlapRight ? -overlapLeft : overlapRight;
  const shiftY = overlapTop < overlapBottom ? -overlapTop : overlapBottom;
  if (Math.abs(shiftX) <= Math.abs(shiftY)) return { x: other.x + shiftX, y: other.y };
  return { x: other.x, y: other.y + shiftY };
}

const defaultTasks = [
  { id: 't1', label: 'Try an app from Explore', completed: false, link: '/apps-store/marketplace' },
  { id: 't2', label: 'Complete a case or quiz', completed: false, link: '/case-studies' },
  { id: 't3', label: 'Check your interactions', completed: false, link: '/apps-store' },
];

const defaultBubbles = [
  /*
  {
    id: 'my-path-bubble',
    type: 'paused_app',
    title: 'My Path',
    subtitle: 'Timeline & Tasks',
    link: '/my-path',
    imageUrl: '/my_path_icon.png',
    x: 40,
    y: 40,
    minimized: false,
    hidden: false
  }
  */
];

function loadJson(key, fallback) {
  try {
    const s = localStorage.getItem(key);
    if (s) return JSON.parse(s);
  } catch (_) {}
  return fallback;
}

function saveJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

function normalizeBubblesFromStorage(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((b, i) => {
    const x = typeof b.x === 'number' ? b.x : getDefaultPosition(i).x;
    const y = typeof b.y === 'number' ? b.y : getDefaultPosition(i).y;
    const minimized = !!b.minimized;
    const hidden = !!b.hidden;
    return { ...b, x, y, minimized, hidden };
  });
}

const AppStoreHomeContext = createContext(null);

export function AppStoreHomeProvider({ children }) {
  const { user } = useAuth();
  const userStorageId = user?.id || user?.email || '';
  const tasksKey = userScopedKey(STORAGE_KEY_TASKS, userStorageId);
  const bubblesKey = userScopedKey(STORAGE_KEY_BUBBLES, userStorageId);

  const [tasks, setTasksState] = useState(defaultTasks);
  const [bubbles, setBubblesState] = useState([]);

  useLayoutEffect(() => {
    if (!userStorageId) return;
    const tk = userScopedKey(STORAGE_KEY_TASKS, userStorageId);
    const bk = userScopedKey(STORAGE_KEY_BUBBLES, userStorageId);
    const loadedTasks = loadJson(tk, defaultTasks);
    setTasksState(
      (Array.isArray(loadedTasks) ? loadedTasks : defaultTasks).slice(0, MAX_TASKS)
    );
    const loadedBubbles = loadJson(bk, defaultBubbles);
    const normalized = normalizeBubblesFromStorage(loadedBubbles).filter(Boolean);
    setBubblesState(normalized);
  }, [userStorageId]);

  useEffect(() => {
    if (!userStorageId) return;
    saveJson(tasksKey, tasks);
  }, [tasks, tasksKey, userStorageId]);

  useEffect(() => {
    if (!userStorageId) return;
    saveJson(bubblesKey, bubbles);
  }, [bubbles, bubblesKey, userStorageId]);

  const setTasks = useCallback((next) => {
    setTasksState((prev) => {
      const list = typeof next === 'function' ? next(prev) : next;
      return (Array.isArray(list) ? list : prev).slice(0, MAX_TASKS);
    });
  }, []);

  const toggleTask = useCallback((taskId) => {
    setTasksState((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t))
    );
  }, []);

  const addTask = useCallback((label, link) => {
    const id = 't' + Date.now();
    setTasksState((prev) => [...prev.slice(-(MAX_TASKS - 1)), { id, label, completed: false, link: link || null }]);
    return id;
  }, []);

  const addBubble = useCallback((bubble) => {
    const id = bubble.id || 'b' + Date.now();
    setBubblesState((prev) => {
      const visible = prev.filter((b) => !b.hidden && !b.minimized);
      let pos = getDefaultPosition(visible.length);
      for (let i = 0; i < 100; i++) {
        const candidate = i === 0 ? pos : getDefaultPosition(visible.length + i);
        const overlaps = visible.some((b) =>
          rectsOverlap(
            { x: candidate.x, y: candidate.y },
            { x: b.x ?? 0, y: b.y ?? 0 }
          )
        );
        if (!overlaps) {
          pos = candidate;
          break;
        }
      }
      const created = {
        id,
        createdAt: Date.now(),
        hidden: false,
        minimized: false,
        ...bubble,
        x: pos.x,
        y: pos.y,
      };
      return [...prev, created];
    });
    return id;
  }, []);

  const hideBubble = useCallback((bubbleId) => {
    setBubblesState((prev) =>
      prev.map((b) => (b.id === bubbleId ? { ...b, hidden: true, minimized: false } : b))
    );
  }, []);

  const deleteBubble = useCallback((bubbleId) => {
    setBubblesState((prev) => prev.filter((b) => b.id !== bubbleId));
  }, []);

  const minimizeBubble = useCallback((bubbleId) => {
    setBubblesState((prev) =>
      prev.map((b) => (b.id === bubbleId ? { ...b, minimized: true } : b))
    );
  }, []);

  const restoreBubble = useCallback((bubbleId) => {
    setBubblesState((prev) =>
      prev.map((b) =>
        b.id === bubbleId ? { ...b, minimized: false, hidden: false } : b
      )
    );
  }, []);

  const updateBubblePosition = useCallback((bubbleId, x, y) => {
    setBubblesState((prev) => {
      const moved = prev.find((b) => b.id === bubbleId);
      if (!moved) return prev;
      let next = prev.map((b) =>
        b.id === bubbleId ? { ...b, x, y } : b
      );
      const movedRect = { x, y };
      for (let pass = 0; pass < 8; pass++) {
        let changed = false;
        next = next.map((b) => {
          if (b.hidden) return b;
          const rect = { x: b.x ?? 0, y: b.y ?? 0 };
          if (b.id === bubbleId) return b;
          if (!rectsOverlap(movedRect, rect)) return b;
          const pushed = pushOut(movedRect, rect);
          if (pushed.x !== rect.x || pushed.y !== rect.y) changed = true;
          return { ...b, x: pushed.x, y: pushed.y };
        });
        for (let i = 0; i < next.length; i++) {
          if (next[i].hidden) continue;
          for (let j = i + 1; j < next.length; j++) {
            if (next[j].hidden) continue;
            const a = { x: next[i].x ?? 0, y: next[i].y ?? 0 };
            const bRect = { x: next[j].x ?? 0, y: next[j].y ?? 0 };
            if (!rectsOverlap(a, bRect)) continue;
            const pushedJ = pushOut(a, bRect);
            next = next.map((n, idx) => (idx === j ? { ...n, x: pushedJ.x, y: pushedJ.y } : n));
            changed = true;
          }
        }
        if (!changed) break;
      }
      return next;
    });
  }, []);

  const value = {
    tasks,
    setTasks,
    toggleTask,
    addTask,
    bubbles: bubbles.filter((b) => !b.hidden && !b.minimized),
    allBubbles: bubbles,
    addBubble,
    hideBubble,
    deleteBubble,
    updateBubblePosition,
    minimizeBubble,
    restoreBubble,
  };

  return (
    <AppStoreHomeContext.Provider value={value}>
      {children}
    </AppStoreHomeContext.Provider>
  );
}

export function useAppStoreHome() {
  const ctx = useContext(AppStoreHomeContext);
  if (!ctx) throw new Error('useAppStoreHome must be used within AppStoreHomeProvider');
  return ctx;
}

/** Add a bubble from outside the provider (e.g. AppExperience Pause). Next load of AppStoreView will show it. */
export function addBubbleToStorage(bubble) {
  const uid = getAppStoreUserStorageId();
  if (!uid) return null;
  const key = userScopedKey(STORAGE_KEY_BUBBLES, uid);
  const list = loadJson(key, []);
  const id = bubble.id || 'b' + Date.now();
  list.push({ id, createdAt: Date.now(), hidden: false, ...bubble });
  saveJson(key, list);
  return id;
}

export default AppStoreHomeContext;
